import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, LogOut, Menu, Moon, Sun, UserRound } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { fetchCurrentUser, logout } from '@/lib/auth';
import { isNative } from '@/lib/native-auth';
import { useUIStore } from '@/stores/ui-store';
import { Avatar } from '@/ui/primitives';

export function Topbar() {
  const { theme, toggleTheme, toggleMobileSidebar } = useUIStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: user } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fecha o menu ao clicar fora.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  async function handleLogout() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch {
      /* ignora — o backend pode redirecionar */
    } finally {
      if (isNative()) {
        // Nativo: limpa cache e navega no SPA embarcado (sem hard reload).
        qc.clear();
        void navigate({ to: '/login' });
      } else {
        // Hard reload pra limpar todo o cache do react-query e voltar pro login.
        window.location.href = '/login';
      }
    }
  }

  return (
    <header id="app-topbar" className="h-[var(--topbar-h)] shrink-0 border-b border-border bg-bg flex items-center gap-3 px-4 md:px-6">
      {/* Hambúrguer — só visível no mobile */}
      <button
        type="button"
        onClick={toggleMobileSidebar}
        aria-label="Abrir menu"
        className="md:hidden size-8 rounded-md hover:bg-surface2 inline-flex items-center justify-center text-text-2 hover:text-text transition-colors"
      >
        <Menu size={18} strokeWidth={1.5} />
      </button>
      <div className="flex-1" />
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={theme === 'light' ? 'Ativar tema escuro' : 'Ativar tema claro'}
        className="size-8 rounded-md hover:bg-surface2 inline-flex items-center justify-center text-text-2 hover:text-text transition-colors"
      >
        {theme === 'light' ? <Moon size={15} strokeWidth={1.5} /> : <Sun size={15} strokeWidth={1.5} />}
      </button>
      {user && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2 rounded-md pl-1 pr-2 py-1 hover:bg-surface2 transition-colors"
          >
            <Avatar name={user.name} color={user.avatar_color ?? '#e96a45'} size="sm" />
            <div className="flex flex-col leading-tight text-left">
              <span className="text-xs font-medium text-text">{user.name}</span>
              <span className="text-[10px] text-text-muted">{user.cargo ?? user.role}</span>
            </div>
            <ChevronDown size={13} strokeWidth={1.5} className="text-text-muted" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+4px)] min-w-[200px] rounded-lg border border-border bg-elevated shadow-lg overflow-hidden z-50"
            >
              <div className="px-3 py-2 border-b border-border">
                <div className="text-xs font-medium text-text truncate">{user.name}</div>
                <div className="text-[11px] text-text-muted truncate">{user.email}</div>
              </div>
              <Link
                to="/perfil"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-text-2 hover:bg-surface2 hover:text-text transition-colors"
              >
                <UserRound size={14} strokeWidth={1.5} />
                Meu perfil
              </Link>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); void handleLogout(); }}
                disabled={signingOut}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-surface2 transition-colors text-left disabled:opacity-60"
              >
                <LogOut size={14} strokeWidth={1.5} />
                {signingOut ? 'Saindo…' : 'Sair'}
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
